import React, { useEffect, useLayoutEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X, ArrowRight, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

const STEPS = [
  { target: "[data-testid='nav-jobs']", route: "/jobs", title: "Your Jobs board", body: "Every job lives here. Jobs a partner shared with you carry a purple \"From …\" chip — their client details stay hidden, everything you need to serve is visible." },
  { target: "[data-testid='jobs-table'], [data-testid='jobs-list'], [data-testid='filter-status-select']", route: "/jobs", title: "Open a job", body: "Click any row to open it. Use the status filter to see Pending, Served, Needs invoicing or Archived. Press / anywhere to search." , noAdvanceNav: true },
  { target: "[data-testid='log-attempt-button']", route: "job", title: "Log attempts", body: "Tap New Attempt to record each try: GPS, photos or video, person description, audio note and signature. On shared jobs your partner is notified instantly." },
  { target: "[data-testid='generate-affidavit-button']", route: "job", title: "Issue the affidavit", body: "Once served, click New Affidavit. Pick the serving server (your roster, a name from the attempts, or type one), review the court form, then sign — or email a one-tap signing link." },
  { target: "[data-testid='billing-documents-section']", route: "job", title: "Invoice & receipt", body: "Attach invoice/receipt PDFs, email them to the client, tick \"Invoice issued\" and mark paid — the Overview tracks what's outstanding." },
  { target: "[data-testid='notification-bell']", route: "any", title: "Partner activity bell", body: "New shares, partner attempts and signed affidavits show up here, so you never rely on email alone. The Partner Guide in the sidebar has the full walkthrough." },
];
const KEY = (uid) => `ss_tour_${uid || "anon"}`;
export const startTour = () => { window.dispatchEvent(new CustomEvent("ss-tour-start")); };

export function GuidedTour() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [step, setStep] = useState(null);
  const [rect, setRect] = useState(null);
  const [dontShow, setDontShow] = useState(true);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    const st = localStorage.getItem(KEY(user.user_id));
    if (user.tour_done) localStorage.setItem(KEY(user.user_id), "done");
    else if (user.role !== "client" && st !== "done") { const n = parseInt(st || "0", 10) || 0; localStorage.setItem(KEY(user.user_id), String(n)); setStep(n); }
    const on = () => { localStorage.setItem(KEY(user.user_id), "0"); setStep(0); nav("/jobs"); };
    window.addEventListener("ss-tour-start", on); return () => window.removeEventListener("ss-tour-start", on);
  }, [user]);

  const s = step != null ? STEPS[step] : null;
  const onRoute = s && (s.route === "any" || (s.route === "job" ? /^\/jobs\/[^/]+$/.test(loc.pathname) && !loc.pathname.endsWith("/new") : loc.pathname.startsWith(s.route)));

  useLayoutEffect(() => {
    if (!s || !onRoute) { setRect(null); return; }
    let tries = 0;
    const find = () => {
      const el = document.querySelector(s.target);
      if (el) { el.scrollIntoView({ block: "center", behavior: "instant" }); const r = el.getBoundingClientRect(); setRect({ top: r.top, left: r.left, width: r.width, height: r.height }); }
      else if (tries++ < 20) setTimeout(find, 150);
      else setRect(null);
    };
    find();
    const re = () => find();
    window.addEventListener("resize", re); return () => window.removeEventListener("resize", re);
  }, [step, onRoute, loc.pathname]);

  if (!s) return null;
  const finish = () => {
    setStep(null);
    if (dontShow) { localStorage.setItem(KEY(user?.user_id), "done"); api.post("/auth/tour-done").catch(() => {}); }
    else localStorage.removeItem(KEY(user?.user_id));
  };
  const next = () => { if (step >= STEPS.length - 1) return finish(); localStorage.setItem(KEY(user?.user_id), String(step + 1)); setStep(step + 1); };

  const needsJob = s.route === "job" && !onRoute;
  const pad = 8;
  const tipTop = rect ? Math.min(window.innerHeight - 220, rect.top + rect.height + 14) : window.innerHeight / 2 - 100;
  const tipLeft = rect ? Math.max(16, Math.min(window.innerWidth - 376, rect.left)) : window.innerWidth / 2 - 180;

  return (
    <div className="fixed inset-0 z-[100]" data-testid="guided-tour">
      {rect && onRoute ? (
        <div className="absolute rounded-lg ring-4 ring-[#2d7bd0] transition-all duration-300 pointer-events-none" style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2, boxShadow: "0 0 0 9999px rgba(11,31,58,0.55)" }} data-testid="tour-spotlight" />
      ) : <div className="absolute inset-0 bg-[#0b1f3a]/55" />}
      <div className="absolute w-[360px] max-w-[calc(100vw-32px)] bg-white rounded-xl shadow-2xl p-4 border border-slate-200" style={{ top: tipTop, left: tipLeft }} data-testid="tour-tooltip">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-[#1f5fa8]"><Sparkles className="h-3.5 w-3.5" />Quick tour · {step + 1} of {STEPS.length}</div>
          <button type="button" onClick={finish} className="text-slate-400 hover:text-slate-700" data-testid="tour-skip" title="Skip tour"><X className="h-4 w-4" /></button>
        </div>
        <div className="font-bold text-slate-900 mt-1.5">{s.title}</div>
        <p className="text-sm text-slate-600 mt-1 leading-relaxed">{s.body}</p>
        {needsJob && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mt-2" data-testid="tour-open-job-hint">Open any job from the list to continue this step.</p>}
        <div className="flex items-center justify-between mt-3 gap-3">
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none"><input type="checkbox" checked={dontShow} onChange={(e) => setDontShow(e.target.checked)} data-testid="tour-dont-show" />Don't show me again</label>
          <div className="flex gap-2">
            <button type="button" onClick={finish} className="text-xs text-slate-500 hover:underline px-1" data-testid="tour-finish-early">Skip</button>
            {s.route !== "any" && !onRoute && s.route !== "job" && <Button size="sm" variant="outline" onClick={() => nav(s.route)} data-testid="tour-go">Take me there</Button>}
            {needsJob && <Button size="sm" variant="outline" onClick={() => nav("/jobs")} data-testid="tour-go-jobs">Go to Jobs</Button>}
            <Button size="sm" className="sm-green" onClick={next} data-testid="tour-next">{step >= STEPS.length - 1 ? "Done" : "Next"}<ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
          </div>
        </div>
      </div>
      <div className="absolute inset-0 -z-10" onClick={(e) => e.stopPropagation()} />
    </div>
  );
}
